import API from '../Services/API';
import Model from './Model';
import State from './State';
import { toQueryString } from "../Services/helpers";
import makeBatcher from './Batcher';
import { useDialogues } from '../Components/Dialogues';

const usePaymentsAccount = (alwaysRefresh, block = false) => {
    return Model({
        block,
        alwaysRefresh,
        returnDataKey: 'account',
        stateKey: ['payments', 'account'],
        fetchData: () => API.get('/payments/paymentAccount'),
        setAdditionalData: (account) => {
            if (account.connectedBanks) {
                State.putIfNotExists(['payment', 'connectedBanks'], account.connectedBanks);
            }
        },
        registerAdditionalMethods: (opts) => {
            return {
                configureAccount: (data) => {
                    return opts.wrapUpdateCall(
                        () => API.post(`/payments/configureAccount`, data),
                        null,
                        true,
                    );
                },
                provideAccountVerification: (data) => {
                    return opts.wrapUpdateCall(
                        () => API.post(`/payments/provideAccountVerification`, data),
                        null,
                        true,
                    );
                },
                updateBankAccount: (data) => {
                    return opts.wrapUpdateCall(
                        () => API.post(`/payments/updateBankAccount`, data),
                        null,
                        true,
                    );
                },
                completeAccountSetup: (data) => {
                    return opts.wrapUpdateCall(
                        () => API.post(`/payments/completeAccountSetup`, data),
                        null,
                        true,
                    );
                },
                updatePaymentOptions: (data) => {
                    return opts.wrapUpdateCall(
                        () => API.post(`/payments/updatePaymentMethods`, data),
                        null,
                        true,
                    );
                },
                updateTransactionPreferences: (data) => {
                    return opts.wrapUpdateCall(
                        () => API.post(`/payments/transactions/preferences`, data),
                        null,
                        true,
                        true,
                    );
                },
            }
        },
        defaultLoadingError: 'Sorry, there was an error loading the payment account.',
    });
}

const useConnectBank = () => {
    return Model({
        returnDataKey: 'banks',
        stateKey: 'supportedBanks',
        fetchData: async () => API.get('payments/supportedBanks'),
        registerAdditionalMethods: (opts) => {
            return {
                connectBank: (bankId, connectedBankId) => {
                    return opts.wrapUpdateCall(
                        () => API.post('/payments/connectBank', {bankId, connectedBankId}),
                        null,
                        true,
                    );
                },
            }
        },
        defaultLoadingError: 'Sorry, there was an error loading the supported banks.',
    })
}

const useConnectedBanks = (alwaysRefresh) => {
    return Model({
        alwaysRefresh,
        returnDataKey: 'connectedBanks',
        stateKey: ['payments', 'connectedBanks'],
        fetchData: () => API.get('/payments/connectedBanks'),
        setAdditionalData: (connectedBanks) => {
            connectedBanks.map(connectedBank => State.putIfNotExists(['payments', 'connectedBanks', connectedBank.id], connectedBank))
        },
        defaultLoadingError: 'Sorry, there was an error loading your bank connections.',
    });
}

const useConnectedBank = (connectedBankId, alwaysRefresh) => {
    return Model({
        alwaysRefresh,
        returnDataKey: 'connectedBank',
        stateKey: ['payments', 'connectedBanks', connectedBankId],
        fetchData: () => API.get('/payments/connectedBanks/' + connectedBankId),
        registerAdditionalMethods: (opts) => {
            return {
                checkConnection: () => {
                    opts.setIsSaving(true);
                    return API.post('/payments/checkConnectedBank/' + connectedBankId).then((response) => {
                        opts.setIsSaving(false);
                        opts.updateModel(response);
                        opts.showSuccess('Connection refreshed');
                        return response;
                    }).catch((err) => {
                        opts.setIsSaving(false);
                        opts.showError(API.parseErrorResponse(err, 'Sorry, something went wrong.'));
                        throw err;
                    });
                },
                deleteConnection: () => {
                    return opts.wrapUpdateCall(
                        () => API.delete('/payments/connectedBanks/' + connectedBankId),
                        null,
                        true,
                        false,
                    );
                },
            }
        },
        defaultLoadingError: 'Sorry, there was an error loading your bank connection.',
    });
}

const usePayments = (searchData, alwaysRefresh = false) => {
    let searchStr = toQueryString(searchData);
    return Model({
        alwaysRefresh: alwaysRefresh,
        returnDataKey: 'payments',
        stateKey: ['payments', searchStr],
        fetchData: async () => API.get(`/payments?${searchStr}`),
        setAdditionalData: (payments) => {
            payments.map(payment => State.putIfNotExists(['payment', payment.id], payment))
        },
        registerAdditionalMethods: (opts) => {
            return {
                matchPaymentToCustomer: (paymentId, customerId) => {
                    return opts.wrapUpdateCall(
                        () => API.post(`/customers/${customerId}/payments/${paymentId}`),
                        null,
                        true,
                    );
                },
                removePaymentFromCustomer: (paymentId, customerId) => {
                    return opts.wrapUpdateCall(
                        () => API.delete(`/customers/${customerId}/payments/${paymentId}`),
                        null,
                        true,
                    );
                },
                refundPayment: (paymentId) => {
                    return opts.wrapUpdateCall(
                        () => API.post(`/payments/${paymentId}/refund`),
                        null,
                        true,
                    );
                },
                deletePayment: (paymentId) => {
                    return opts.wrapUpdateCall(
                        () => API.delete(`/payments/${paymentId}`),
                        null,
                        true,
                    );
                }
            }
        },
        defaultLoadingError: 'Sorry, there was an error loading your payments.',
    });
}

const paymentsBatchLoader = makeBatcher(
    'payments',
    (paymentIds) => '/payments?'+toQueryString({paymentIds}),
    'id',
    'There was an error loading payments.',
    20,
    100,
)

const usePayment = (id, alwaysRefresh = false, allowBatching = false) => {
    const {showError} = useDialogues();

    return Model({
        alwaysRefresh: alwaysRefresh,
        returnDataKey: 'payment',
        stateKey: ['payment', id],
        block: !id,
        fetchData: async () => {
            if (allowBatching) {
                return paymentsBatchLoader(id, showError);
            }

            return API.get('/payments/'+id)
        },
        registerAdditionalMethods: (opts) => {
            return {
                removePaymentFromCustomer: () => {
                    const payment = opts.model();
                    return opts.wrapUpdateCall(
                        () => API.delete(`/customers/${payment.customerId}/payments/${id}`),
                        null,
                        true,
                    );
                }
            }
        },
        defaultLoadingError: 'Sorry, there was an error loading your payment.',
    });
}

const useTransactions = (searchData, alwaysRefresh = false) => {
    let searchStr = toQueryString(searchData);

    return Model({
        alwaysRefresh: alwaysRefresh,
        returnDataKey: 'transactions',
        stateKey: ['transactions', searchStr],
        fetchData: async () => API.get(`/payments/transactions?${searchStr}`),
        setAdditionalData: (transactions) => {
            transactions.map(transaction => State.putIfNotExists(['transaction', transaction.internalTransactionId], transaction))
        },
        registerAdditionalMethods: (opts) => {
            return {
                resetTransaction: (transaction) => {
                    return changeTransaction(opts, transaction, 'reset', 'New', {});
                },
                ignoreTransaction: (transaction) => {
                    return changeTransaction(opts, transaction, 'ignore', 'Ignored', {});
                },
                convertTransactionForCustomer: (transaction, customerId) => {
                    return changeTransaction(opts, transaction, 'convert', 'Converted', { customerId });
                },
                ignoreAllNew: massTransactionAction(opts, 'ignoreAllNew'),
                confirmAllMatched: massTransactionAction(opts, 'convertAllMatched'),
            }
        },
        defaultLoadingError: 'Sorry, there was an error loading your transactions.',
    });
}

const massTransactionAction = (opts, path) => {
    return () => {
        opts.setIsSaving(true);
        return new Promise((resolve, reject) => {
            API.post(`/payments/transactions/${path}`).then(response => {
                opts.refresh();
                resolve(response);
                opts.setIsSaving(false);
            }).catch(err => {
                opts.showError(API.parseErrorResponse(err));
                reject(API.parseErrorResponse(err));
                opts.setIsSaving(false);
            })
        })
    }
}

const changeTransaction = (opts, transaction, method, newStatus, data) => {
    opts.setIsSaving(true);
    return new Promise((resolve, reject) => {
        API.post(
            `/payments/transactions/${transaction.internalTransactionId}/${method}`, data
        ).then(response => {
            State.put(
                ['transaction', transaction.internalTransactionId],
                Object.assign({}, transaction, { newStatus: newStatus })
            );
            opts.updateModel(
                opts.model().map(trans => {
                    if (trans.internalTransactionId === transaction.internalTransactionId) {
                        trans.newStatus = newStatus;
                        if (data.customerId) {
                            trans.customerId = data.customerId;
                        }
                        if (method === 'reset' || method === 'ignore') {
                            trans.paymentId = null;
                            trans.customerId = null;
                        }
                    }
                    return trans;
                })
            );

            resolve(response);
            opts.setIsSaving(false);
        }).catch(err => {
            opts.showError(API.parseErrorResponse(err));
            reject(API.parseErrorResponse(err));
            opts.setIsSaving(false);
        })
    })
}

const changeTransactionCombined = (opts, transaction, method, newStatus, data) => {
    opts.setIsSaving(true);
    return new Promise((resolve, reject) => {
        API.post(
            `/payments/transactions/${transaction.internalTransactionId}/${method}`, data
        ).then(response => {
            State.put(
                ['transaction', transaction.internalTransactionId],
                Object.assign({}, transaction, { newStatus: newStatus })
            );
            opts.updateModel(
                opts.model().map(item => {
                    if (item.transaction && item.transaction.internalTransactionId === transaction.internalTransactionId) {
                        item.transaction = Object.assign({}, item.transaction, { newStatus: newStatus });
                        if (data.customerId) {
                            item.transaction.customerId = data.customerId;
                        }
                        if (method === 'reset' || method === 'ignore') {
                            item.transaction.paymentId = null;
                            item.transaction.customerId = null;
                        }
                    }
                    return item;
                })
            );

            resolve(response);
            opts.setIsSaving(false);
        }).catch(err => {
            opts.showError(API.parseErrorResponse(err));
            reject(API.parseErrorResponse(err));
            opts.setIsSaving(false);
        })
    })
}

const useCombinedPayments = (searchData, alwaysRefresh = false) => {
    let searchStr = toQueryString(searchData);

    return Model({
        alwaysRefresh: alwaysRefresh,
        returnDataKey: 'items',
        stateKey: ['payments', 'combined', searchStr],
        fetchData: async () => API.get(`/payments/combined?${searchStr}`),
        setAdditionalData: (items) => {
            items.forEach(item => {
                if (item.payment) {
                    State.putIfNotExists(['payment', item.payment.id], item.payment);
                }
                if (item.transaction) {
                    State.putIfNotExists(['transaction', item.transaction.internalTransactionId], item.transaction);
                }
            });
        },
        registerAdditionalMethods: (opts) => {
            return {
                matchPaymentToCustomer: (paymentId, customerId) => {
                    return opts.wrapUpdateCall(
                        () => API.post(`/customers/${customerId}/payments/${paymentId}`),
                        null,
                        true,
                    );
                },
                removePaymentFromCustomer: (paymentId, customerId) => {
                    return opts.wrapUpdateCall(
                        () => API.delete(`/customers/${customerId}/payments/${paymentId}`),
                        null,
                        true,
                    );
                },
                refundPayment: (paymentId) => {
                    return opts.wrapUpdateCall(
                        () => API.post(`/payments/${paymentId}/refund`),
                        null,
                        true,
                    );
                },
                deletePayment: (paymentId) => {
                    return opts.wrapUpdateCall(
                        () => API.delete(`/payments/${paymentId}`),
                        null,
                        true,
                    );
                },
                resetTransaction: (transaction) => {
                    return changeTransactionCombined(opts, transaction, 'reset', 'New', {});
                },
                ignoreTransaction: (transaction) => {
                    return changeTransactionCombined(opts, transaction, 'ignore', 'Ignored', {});
                },
                convertTransactionForCustomer: (transaction, customerId) => {
                    return changeTransactionCombined(opts, transaction, 'convert', 'Converted', { customerId });
                },
                ignoreAllNew: massTransactionAction(opts, 'ignoreAllNew'),
                confirmAllMatched: massTransactionAction(opts, 'convertAllMatched'),
            }
        },
        defaultLoadingError: 'Sorry, there was an error loading payments and transactions.',
    });
}

const useStats = (dateRange, alwaysRefresh = false) => {
    return Model({
        alwaysRefresh: alwaysRefresh,
        returnDataKey: 'stats',
        stateKey: ['payments', 'stats', dateRange],
        fetchData: () => API.get('/payments/stats?dateRange=' + dateRange),
        defaultLoadingError: 'Sorry, there was an error loading payment stats.',
    });
}

const externalPaymentMethods = [
    {value: 'External Cash', label: 'Cash'},
    {value: 'External Cheque', label: 'Cheque'},
    {value: 'External Transfer', label: 'Transfer'},
    {value: 'External Card', label: 'Card'},
    {value: 'External Other', label: 'Other'},
];


export {
    usePaymentsAccount,
    usePayments,
    usePayment,
    useTransactions,
    useCombinedPayments,
    useConnectBank,
    useConnectedBanks,
    useConnectedBank,
    useStats,
    externalPaymentMethods,
}
